import React, { useState, useEffect } from "react";
import * as api from "../../services/api";
import "./UnitsManagement.css";

export default function UnitsManagement() {
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unitName, setUnitName] = useState("");
  const [editingUnit, setEditingUnit] = useState(null);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    loadUnits();
  }, []);

  const loadUnits = async () => {
    try {
      setLoading(true);
      const unitsData = await api.getUnits();
      setUnits(unitsData);
    } catch (error) {
      console.error('Error loading units:', error);
      setError('Failed to load units');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!unitName.trim()) {
      setError("Unit name is required");
      return;
    }

    try {
      if (editingUnit) {
        await api.updateUnit(editingUnit._id, { name: unitName.trim() });
      } else {
        await api.createUnit({ name: unitName.trim() });
      }
      setUnitName("");
      setEditingUnit(null);
      loadUnits();
    } catch (error) {
      console.error('Error saving unit:', error);
      setError(error.response?.data?.message || 'Failed to save unit');
    }
  };

  const handleEdit = (unit) => {
    setEditingUnit(unit);
    setUnitName(unit.name);
    setError("");
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this unit?')) return;
    try {
      await api.deleteUnit(id);
      loadUnits();
    } catch (error) {
      console.error('Error deleting unit:', error);
      alert(error.response?.data?.message || 'Failed to delete unit');
    }
  };

  const handleCancel = () => {
    setEditingUnit(null);
    setUnitName("");
    setError("");
  };

  // Filter units based on search query
  const filteredUnits = units.filter(unit => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (unit.name || "").toLowerCase().includes(query);
  });

  if (loading) {
    return <div className="units-loading">Loading units...</div>;
  }

  return (
    <div className="units-management">
      <div className="units-container">
        {/* Left Panel - Add/Edit Unit */}
        <div className="units-left-panel">
          <h2>Unit</h2>
          <form onSubmit={handleSubmit} className="unit-form">
            {error && <div className="error-message">{error}</div>}
            <div className="form-group">
              <label className="unit-label">Unit Name</label>
              <input
                type="text"
                className="unit-input"
                placeholder="Enter Category"
                value={unitName}
                onChange={(e) => setUnitName(e.target.value)}
                required
              />
            </div>
            <div className="form-actions">
              {editingUnit && (
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={handleCancel}
                >
                  Cancel
                </button>
              )}
              <button type="submit" className="btn-add-unit">
                {editingUnit ? 'Update Unit' : 'Add Unit'}
              </button>
            </div>
          </form>
        </div>

        {/* Right Panel - Unit List */}
        <div className="units-right-panel">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '1rem' }}>
            <h2>Unit List</h2>
            <div style={{ flex: 1, maxWidth: '400px', minWidth: '200px' }}>
              <input
                type="text"
                placeholder="🔍 Search units..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="units-search-input"
              />
            </div>
          </div>
          {searchQuery && (
            <div style={{ marginBottom: '1rem', color: '#666', fontSize: '0.9rem' }}>
              Showing {filteredUnits.length} of {units.length} units
            </div>
          )}
          <div className="units-table-container">
            <table className="units-table">
              <thead>
                <tr>
                  <th>Sl No.</th>
                  <th>Unit</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredUnits.length === 0 ? (
                  <tr>
                    <td colSpan="3" className="empty-state">
                      {units.length === 0 
                        ? "No units found. Add your first unit!" 
                        : "No units match your search criteria."}
                    </td>
                  </tr>
                ) : (
                  filteredUnits.map((unit, index) => (
                    <tr key={unit._id}>
                      <td>{index + 1}</td>
                      <td>{unit.name}</td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn-edit"
                            onClick={() => handleEdit(unit)}
                            title="Edit Unit"
                          >
                            ✏️
                          </button>
                          <button
                            className="btn-delete"
                            onClick={() => handleDelete(unit._id)}
                            title="Delete Unit"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

